import { BUS_LINES } from "./collection.js";
import { escapeHtml } from "./utils.js";

export async function loadJson(path) {
  const response = await fetch(path, { cache: "no-store" });
  if (!response.ok) throw new Error(`Não foi possível carregar ${path} (HTTP ${response.status}).`);
  return response.json();
}

export function renderRadioOptions(question, currentValue, columns = 2, disabled = false) {
  return `
    <div class="radio-grid" style="--option-columns:${columns}" data-question-id="${escapeHtml(question.id)}">
      ${question.options.map(option => `
        <label class="radio-option">
          <input type="radio" name="${escapeHtml(question.id)}" value="${escapeHtml(option.value)}" ${currentValue === option.value ? "checked" : ""} ${disabled ? "disabled" : ""} required>
          <span>${escapeHtml(option.label)}</span>
        </label>
      `).join("")}
    </div>
  `;
}

export function renderLikertSection(section, scale, answers = {}) {
  return `
    <div class="likert-list">
      ${section.items.map((item, index) => `
        <fieldset class="likert-question ${answers[item.id] ? "answered" : ""}" data-question-id="${escapeHtml(item.id)}">
          <legend>${index + 1}. ${escapeHtml(item.text)}</legend>
          <div class="likert-scale">
            ${scale.map(point => `
              <label class="likert-option" title="${escapeHtml(point.label)}">
                <input type="radio" name="${escapeHtml(item.id)}" value="${point.value}" ${Number(answers[item.id]) === Number(point.value) ? "checked" : ""} required>
                <span class="likert-value">${point.value}</span>
                <span>${escapeHtml(point.label)}</span>
              </label>
            `).join("")}
          </div>
        </fieldset>
      `).join("")}
    </div>
  `;
}

export function readRequiredRadio(formData, id, label) {
  const value = formData.get(id);
  if (!value) throw new Error(`Responda: ${label}`);
  return String(value);
}

export function validateFieldContext(formData, questionnaire, existing = {}) {
  const context = questionnaire.field_context;
  const approachType = readRequiredRadio(
    formData,
    context.approach_type.id,
    context.approach_type.label
  );

  if (approachType === "embarcado") {
    const lineCode = String(formData.get(context.bus_line.id) || "");
    const line = BUS_LINES.find(option => option.value === lineCode);
    if (!line && !lineCode && existing.approach_type === "embarcado" && existing.line_or_location && !BUS_LINES.some(option => option.value === existing.line_or_location)) return { ...existing };
    if (!line) throw new Error(`Informe: ${context.bus_line.label}`);
    const treatmentGroup = line.treatment_group === "pitaguari" ? "pitaguari" : "controle";
    return {
      treatment_group: treatmentGroup,
      TRAT_i: treatmentGroup === "pitaguari" ? 1 : 0,
      approach_type: approachType,
      line_or_location: line.value,
      bus_line_label: line.label,
      pitaguari_serves_stop: null,
      reference_station: null
    };
  }

  const stopAnswer = readRequiredRadio(
    formData,
    context.pitaguari_serves_stop.id,
    context.pitaguari_serves_stop.label
  );
  const pitaguariServesStop = stopAnswer === "sim";
  const treatmentGroup = pitaguariServesStop ? "pitaguari" : "controle";
  return {
    treatment_group: treatmentGroup,
    TRAT_i: treatmentGroup === "pitaguari" ? 1 : 0,
    approach_type: approachType,
    line_or_location: "Parada de ônibus",
    bus_line_label: null,
    pitaguari_serves_stop: pitaguariServesStop,
    reference_station: null
  };
}

export function validateProfile(formData, questionnaire) {
  const genderQuestion = questionnaire.profile.gender;
  const ageQuestion = questionnaire.profile.age;
  const gender = readRequiredRadio(formData, genderQuestion.id, genderQuestion.label);
  const age = Number(formData.get(ageQuestion.id));

  if (!Number.isInteger(age) || age < ageQuestion.min || age > ageQuestion.max) {
    throw new Error(`Informe uma idade entre ${ageQuestion.min} e ${ageQuestion.max} anos.`);
  }

  return { gender_identity: gender, age_years: age };
}

export function neighborhoodSelectionValue(municipalityId, neighborhood) {
  return `${municipalityId}::${neighborhood}`;
}

export function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("pt-BR")
    .trim();
}

export function neighborhoodEntries(catalog) {
  return (catalog?.municipalities || []).flatMap(municipality =>
    municipality.neighborhoods.map(neighborhood => ({
      municipality,
      neighborhood,
      value: neighborhoodSelectionValue(municipality.id, neighborhood)
    }))
  );
}

export function filterNeighborhoodEntries(catalog, query = "") {
  const normalizedQuery = normalizeSearchText(query);
  const entries = neighborhoodEntries(catalog);
  if (!normalizedQuery) return entries;
  return entries.filter(item =>
    normalizeSearchText(`${item.neighborhood} ${item.municipality.name}`).includes(normalizedQuery)
  );
}

export function resolveNeighborhoodSelection(rawValue, catalog, existing = {}) {
  const value = String(rawValue || "");
  if (value === "outro") {
    const name = String(existing.origin_neighborhood_other || "").trim();
    const city = String(existing.origin_city_other || "").trim();
    if (!name) throw new Error("Informe o nome do bairro.");
    if (!city || city.length > 120) throw new Error("Informe o município (até 120 caracteres).");
    return { origin_city: city, origin_neighborhood: name, origin_neighborhood_category: "outro", origin_neighborhood_other: name, neighborhood_catalog_version: Number(catalog.catalog_version) };
  }
  const separator = value.indexOf("::");
  if (separator < 1) throw new Error("Selecione o bairro de moradia.");

  const municipalityId = value.slice(0, separator);
  const neighborhood = value.slice(separator + 2);
  const municipality = catalog?.municipalities?.find(item => item.id === municipalityId);
  if (municipality?.neighborhoods?.includes(neighborhood)) {
    return {
      origin_city: municipality.name,
      origin_neighborhood: neighborhood,
      origin_neighborhood_category: "catalogo",
      origin_neighborhood_other: null,
      neighborhood_catalog_version: Number(catalog.catalog_version)
    };
  }

  if (
    municipalityId === "legacy" &&
    neighborhood === existing.origin_neighborhood &&
    existing.origin_neighborhood
  ) {
    return {
      origin_city: existing.origin_city || "Não informado",
      origin_neighborhood: neighborhood,
      neighborhood_catalog_version: existing.neighborhood_catalog_version || null
    };
  }

  throw new Error("O bairro selecionado não pertence ao catálogo atual.");
}

export function renderNeighborhoodOptions(catalog, current = {}, query = "", selectedValue = "") {
  const matches = catalog?.municipalities
    ?.flatMap(municipality => municipality.neighborhoods.map(neighborhood => ({ municipality, neighborhood })))
    .filter(item => item.neighborhood === current.origin_neighborhood) || [];
  const currentValue = current.origin_city
    ? matches.find(item => item.municipality.name === current.origin_city)
    : (matches.length === 1 ? matches[0] : null);

  const effectiveSelectedValue = selectedValue || (current.origin_neighborhood_category === "outro" ? "outro" : "") || (
    currentValue
      ? neighborhoodSelectionValue(currentValue.municipality.id, currentValue.neighborhood)
      : ""
  );
  const filtered = filterNeighborhoodEntries(catalog, query);
  const visibleValues = new Set(filtered.map(item => item.value));
  if (effectiveSelectedValue && !visibleValues.has(effectiveSelectedValue)) {
    const selectedEntry = neighborhoodEntries(catalog).find(item => item.value === effectiveSelectedValue);
    if (selectedEntry) filtered.unshift(selectedEntry);
  }

  const groups = (catalog?.municipalities || []).map(municipality => {
    const neighborhoods = filtered.filter(item => item.municipality.id === municipality.id);
    if (!neighborhoods.length) return "";
    return `
      <optgroup label="${escapeHtml(municipality.name)}">
        ${neighborhoods.map(item =>
          `<option value="${escapeHtml(item.value)}" ${effectiveSelectedValue === item.value ? "selected" : ""}>${escapeHtml(item.neighborhood)}</option>`
        ).join("")}
      </optgroup>
    `;
  }).join("");

  const legacy = current.origin_neighborhood && !currentValue && current.origin_neighborhood_category !== "outro"
    ? `<option value="${escapeHtml(neighborhoodSelectionValue("legacy", current.origin_neighborhood))}" selected>${escapeHtml(current.origin_neighborhood)} (registro anterior)</option>`
    : "";

  const emptyLabel = filtered.length ? "Selecione um bairro" : "Nenhum bairro encontrado";
  return `<option value="" ${effectiveSelectedValue || current.origin_neighborhood ? "" : "selected"} disabled>${emptyLabel}</option><option value="outro" ${effectiveSelectedValue === "outro" ? "selected" : ""}>Outros</option>${legacy}${groups}`;
}

export function validateMobility(formData, questionnaire, catalog, existing = {}) {
  const habitQuestion = questionnaire.mobility.metro_habit;
  const neighborhoodQuestion = questionnaire.mobility.neighborhood;
  const metroHabit = readRequiredRadio(formData, habitQuestion.id, habitQuestion.label);
  const location = resolveNeighborhoodSelection(formData.get(neighborhoodQuestion.id), catalog, { ...existing, origin_neighborhood_other: formData.get("origin_neighborhood_other"), origin_city_other: formData.get("origin_city_other") });
  if (location.origin_neighborhood.length > neighborhoodQuestion.max_length) {
    throw new Error(`O bairro deve ter no máximo ${neighborhoodQuestion.max_length} caracteres.`);
  }

  return { metro_habit: metroHabit, ...location };
}

export function validatePsychometrics(formData, section) {
  return Object.fromEntries(section.items.map(item => {
    const rawValue = formData.get(item.id);
    if (!rawValue) throw new Error("Responda todas as afirmações antes de continuar.");
    return [item.id, Number(rawValue)];
  }));
}

export function validateCompleteInterview(interview, questionnaire) {
  const errors = [];
  if (interview.duplicate_attempt || interview.responses?.recent_participation === "sim") throw new Error("Participação duplicada: encerrar sem concluir entrevista válida.");
  if (interview.collectionVersion >= 2 && interview.responses?.recent_participation !== "nao") errors.push("participação recente");
  if (interview.responses?.mobility?.origin_neighborhood_category === "outro" && !interview.responses.mobility.origin_neighborhood_other?.trim()) errors.push("nome do bairro");
  const fieldContext = interview.responses?.field_context || {};
  if (!fieldContext.treatment_group) errors.push("grupo da abordagem");
  if (!fieldContext.approach_type) errors.push("local da abordagem");
  if (fieldContext.approach_type === "embarcado" && !fieldContext.line_or_location) errors.push("linha de ônibus");
  if (fieldContext.approach_type === "ponto" && fieldContext.pitaguari_serves_stop == null) errors.push("passagem da Pitaguary na parada");
  if (!interview.responses?.profile?.gender_identity) errors.push("gênero");
  if (!Number.isInteger(interview.responses?.profile?.age_years)) errors.push("idade");
  if (Object.keys(interview.responses?.choices || {}).length !== interview.designSnapshot.tasks.length) errors.push("cenários");
  if (!interview.responses?.mobility?.metro_habit) errors.push("hábito com o metrô");
  if (!interview.responses?.mobility?.origin_city) errors.push("município");
  if (!interview.responses?.mobility?.origin_neighborhood) errors.push("bairro");

  for (const [kind, section] of Object.entries(questionnaire.psychometrics)) {
    if (!section?.items) continue;
    if (Object.keys(interview.responses?.psychometrics?.[kind] || {}).length !== section.items.length) {
      errors.push(section.title.toLocaleLowerCase("pt-BR"));
    }
  }

  if (errors.length) throw new Error(`Respostas incompletas: ${errors.join(", ")}.`);
  return true;
}

export function findFirstIncomplete(interview, questionnaire) {
  if (interview.collectionVersion >= 2 && interview.responses?.recent_participation !== "nao") return { route: "survey-context", focusName: "recent_participation", message: "Confirme se a pessoa já participou recentemente desta pesquisa." };
  const fieldContext = interview.responses?.field_context || {};
  if (!fieldContext.approach_type) {
    return {
      route: "survey-context",
      focusName: questionnaire.field_context.approach_type.id,
      message: "Informe onde a coleta está sendo feita."
    };
  }
  if (fieldContext.approach_type === "embarcado" && !fieldContext.line_or_location) {
    return {
      route: "survey-context",
      focusName: questionnaire.field_context.bus_line.id,
      message: "Selecione a linha de ônibus da coleta."
    };
  }
  if (fieldContext.approach_type === "ponto" && fieldContext.pitaguari_serves_stop == null) {
    return {
      route: "survey-context",
      focusName: questionnaire.field_context.pitaguari_serves_stop.id,
      message: "Informe se a linha Pitaguary passa nesta parada."
    };
  }

  const profile = interview.responses?.profile || {};
  const age = Number(profile.age_years);
  if (!profile.gender_identity) {
    return {
      route: "survey-profile",
      focusName: questionnaire.profile.gender.id,
      message: "Falta informar o gênero. Marque uma opção para continuar."
    };
  }
  if (
    !Number.isInteger(age)
    || age < questionnaire.profile.age.min
    || age > questionnaire.profile.age.max
  ) {
    return {
      route: "survey-profile",
      focusName: questionnaire.profile.age.id,
      message: `Informe uma idade entre ${questionnaire.profile.age.min} e ${questionnaire.profile.age.max} anos.`
    };
  }

  const mobility = interview.responses?.mobility || {};
  if (!mobility.metro_habit) {
    return {
      route: "survey-profile",
      focusName: questionnaire.mobility.metro_habit.id,
      message: "Falta informar a frequência de uso do metrô."
    };
  }
  if (!mobility.origin_city || !mobility.origin_neighborhood || (mobility.origin_neighborhood_category === "outro" && !mobility.origin_neighborhood_other?.trim())) {
    return {
      route: "survey-profile",
      focusName: questionnaire.mobility.neighborhood.id,
      message: "Falta selecionar o bairro onde a pessoa mora."
    };
  }

  const tasks = interview.designSnapshot?.tasks || [];
  const missingChoiceIndex = tasks.findIndex(task => !interview.responses?.choices?.[task.task_id]);
  if (missingChoiceIndex >= 0) {
    return {
      route: "survey-choice",
      choiceIndex: missingChoiceIndex,
      message: `Falta escolher uma alternativa no cenário ${missingChoiceIndex + 1}.`
    };
  }

  for (const [kind, section] of Object.entries(questionnaire.psychometrics)) {
    if (!section?.items) continue;
    const answers = interview.responses?.psychometrics?.[kind] || {};
    const missingItem = section.items.find(item => !answers[item.id]);
    if (missingItem) {
      return {
        route: `survey-${kind}`,
        focusName: missingItem.id,
        message: `Falta responder uma afirmação de ${section.title.toLocaleLowerCase("pt-BR")}.`
      };
    }
  }

  return null;
}

export function optionLabel(question, value) {
  return question.options.find(option => option.value === value)?.label || value || "—";
}
